import React from 'react';

// ── Types ──────────────────────────────────────────────────────────────

export type ToastType = 'success' | 'error' | 'info' | 'warning';

export interface Toast {
  id: string;
  type: ToastType;
  message: string;
  duration: number; // ms, 0 = ручное закрытие
}

interface ToastContextValue {
  toasts: Toast[];
  showToast:    (type: ToastType, message: string, duration?: number) => void;
  showSuccess:  (message: string, duration?: number) => void;
  showError:    (message: string, duration?: number) => void;
  showInfo:     (message: string, duration?: number) => void;
  showWarning:  (message: string, duration?: number) => void;
  removeToast:  (id: string) => void;
}

// ── Context ────────────────────────────────────────────────────────────

const ToastContext = React.createContext<ToastContextValue | null>(null);

let _counter = 0;
function nextId(): string {
  return `toast_${++_counter}_${Date.now()}`;
}

const DEFAULT_DURATION: Record<ToastType, number> = {
  success: 4000,
  error:   6000,
  info:    4000,
  warning: 5000,
};

// ── Provider ───────────────────────────────────────────────────────────

export const ToastProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [toasts, setToasts] = React.useState<Toast[]>([]);

  const removeToast = React.useCallback((id: string) => {
    setToasts(prev => prev.filter(t => t.id !== id));
  }, []);

  const showToast = React.useCallback((type: ToastType, message: string, duration?: number) => {
    const id = nextId();
    const dur = duration ?? DEFAULT_DURATION[type];
    setToasts(prev => [...prev, { id, type, message, duration: dur }]);
    if (dur > 0) {
      setTimeout(() => removeToast(id), dur);
    }
  }, [removeToast]);

  const showSuccess = React.useCallback((msg: string, dur?: number) => showToast('success', msg, dur), [showToast]);
  const showError   = React.useCallback((msg: string, dur?: number) => showToast('error',   msg, dur), [showToast]);
  const showInfo    = React.useCallback((msg: string, dur?: number) => showToast('info',    msg, dur), [showToast]);
  const showWarning = React.useCallback((msg: string, dur?: number) => showToast('warning', msg, dur), [showToast]);

  return (
    <ToastContext.Provider value={{ toasts, showToast, showSuccess, showError, showInfo, showWarning, removeToast }}>
      {children}
    </ToastContext.Provider>
  );
};

// ── Hook ───────────────────────────────────────────────────────────────

export function useToast(): ToastContextValue {
  const ctx = React.useContext(ToastContext);
  if (!ctx) throw new Error('useToast must be used inside ToastProvider');
  return ctx;
}